import type {
  OperationExecutionEventStatus,
  OperationExecutionGraph,
  OperationExecutionKind,
  OperationExecutionRefs,
  OperationExecutionState,
  OperationExecutionViewKind,
  OperationExecutionWithdrawRef,
} from "@repo/ports/v2";
import {
  EXECUTION_ACTIVITY_EVENT_KINDS,
  EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN,
  EXECUTION_ACTIVITY_PHASES,
} from "@repo/ports/v2/operation-execution/activity-semantics";
import { z } from "zod";
import {
  ExpectedMarketOrderVenueSchema,
  LifiMarketOrderVenueKeySchema,
  NonNegativeIntegerAmountSchema,
  PositiveIntegerAmountSchema,
  SpecDrivenBuyFundLegWireSchema,
  SpecDrivenBuyPlanBasisSchema,
} from "./requests.js";

const JsonPrimitiveSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

type JsonPrimitive = string | number | boolean | null;
export type ExecutionLiveJsonValue =
  | JsonPrimitive
  | readonly ExecutionLiveJsonValue[]
  | { readonly [key: string]: ExecutionLiveJsonValue };

export type ExecutionLiveRefs = Omit<
  OperationExecutionRefs,
  "executionIntent" | "withdraw" | "workflowParams" | "workflowVersion"
> & {
  readonly executionIntent?: never;
  readonly workflowParams?: never;
  readonly workflowVersion?: never;
  readonly withdraw?:
    | (Omit<OperationExecutionWithdrawRef, "recipientAddress"> & {
        readonly recipientAddress?: string;
      })
    | null;
} & {
  // `| undefined` keeps the forward-compat catchall compatible with the
  // declared optional properties above: without it no object with an absent
  // optional ref is assignable to this type under strict index-signature
  // checking (webapp builds construct refs objects by spreading partials).
  readonly [key: string]: ExecutionLiveJsonValue | undefined;
};

export const ExecutionLiveJsonValueSchema: z.ZodType<ExecutionLiveJsonValue> =
  z.lazy(() =>
    z.union([
      JsonPrimitiveSchema,
      z.array(ExecutionLiveJsonValueSchema),
      // Use the two-argument form so this leaf package can load under either
      // the repository's Zod 3 dependency or a Zod 4-deduped app bundle.
      z.record(z.string(), ExecutionLiveJsonValueSchema),
    ]),
  );

export const ExecutionLiveJsonObjectSchema = z.record(
  z.string(),
  ExecutionLiveJsonValueSchema,
);

export const ExecutionStateSchema = z.enum([
  "accepted",
  "running",
  "awaiting_user",
  "retrying",
  "completed",
  "failed",
  "cancelled",
]);

export const LiveDescriptorSchema = z.object({
  transport: z.literal("websocket"),
  path: z.string().min(1),
  url: z.string().min(1),
  requiresToken: z.boolean().optional(),
  token: z.string().min(1).optional(),
  expiresAt: z.string().min(1).optional(),
});

export const UserFeedLiveDescriptorSchema = LiveDescriptorSchema.extend({
  userId: z.string().min(1),
});

export const PortfolioFeedLiveDescriptorSchema = LiveDescriptorSchema.extend({
  portfolioId: z.string().min(1),
});

export const ExecutionOperationResponseSchema = z.object({
  operationId: z.string().min(1),
  portfolioId: z.string().min(1).nullable(),
  requestKey: z.string().min(1).nullable(),
  kind: z.enum(["deposit", "execution", "transfer", "withdraw", "bridge"]),
  state: ExecutionStateSchema,
  workflowId: z.string().min(1),
  workflowStatus: z.string().min(1),
  currentHeadline: z.string().nullable(),
  currentDetail: z.string().nullable(),
  currentPhase: z.string().nullable(),
  activityPhase: z.enum(EXECUTION_ACTIVITY_PHASES).optional(),
  failureCode: z
    .string()
    .regex(EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN)
    .nullable()
    .optional(),
  currentStep: z.number().nullable(),
  totalSteps: z.number().nullable(),
  output: z.unknown().nullable().optional(),
  error: z
    .object({
      name: z.string(),
      message: z.string(),
    })
    .nullable()
    .optional(),
  createdAt: z.string().min(1).optional(),
  updatedAt: z.string().min(1).optional(),
  finishedAt: z.string().nullable().optional(),
  flow: z.string().optional(),
  provider: z.string().optional(),
  deduped: z.boolean().optional(),
  queueDisposition: z.enum(["started", "deduped_existing"]).optional(),
  actionTypes: z.array(z.string()).optional(),
  completionTarget: z.enum(["submitted", "confirmed", "settled"]).optional(),
  live: LiveDescriptorSchema.optional(),
  portfolioLive: LiveDescriptorSchema.optional(),
  portfolioLives: z.array(PortfolioFeedLiveDescriptorSchema).optional(),
});

export const ExecutionRebalanceCreateResponseSchema =
  ExecutionOperationResponseSchema.extend({
    flow: z.literal("rebalance"),
    planHash: z.string().min(1),
    waveCount: z.number().int().nonnegative(),
    previewScope: z.literal("authoritative"),
    strategyHash: z.string().min(1).optional(),
    capitalUnallocatedUsd: z.number().nullable().optional(),
  });

export const ExecutionLiquidationCreateResponseSchema =
  ExecutionOperationResponseSchema.extend({
    flow: z.literal("liquidation"),
    specHash: z.string().min(1),
  });

export const ExecutionLiquidationOperationAckSchema = z.object({
  operationId: z.string().min(1),
  specHash: z.string().min(1),
  workflowId: z.string().min(1).optional(),
  runId: z.string().min(1).optional(),
});

// Response-only address validation preserves historical wire casing. Request
// address normalization is owned by the shared request schemas.
const EvmAddressSchema = z.string().regex(/^0x[a-fA-F0-9]{40}$/u);

export const OndoDirectRebalancePlanSchema = z.object({
  totalValueCashRaw: NonNegativeIntegerAmountSchema,
  cashInputAmount: NonNegativeIntegerAmountSchema.nullable(),
  sells: z.array(
    z.object({
      assetId: z.string(),
      tokenAddress: EvmAddressSchema,
      amount: NonNegativeIntegerAmountSchema,
      estimatedCashOutRaw: NonNegativeIntegerAmountSchema,
      fullExit: z.boolean(),
    }),
  ),
  buys: z.array(
    z.object({
      assetId: z.string(),
      tokenAddress: EvmAddressSchema,
      cashAmount: NonNegativeIntegerAmountSchema,
    }),
  ),
  skipped: z.array(
    z.object({
      assetId: z.string(),
      side: z.enum(["sell", "buy"]),
      reason: z.string(),
      cashNotionalRaw: NonNegativeIntegerAmountSchema,
    }),
  ),
  assets: z.array(
    z.object({
      assetId: z.string(),
      currentValueCashRaw: NonNegativeIntegerAmountSchema,
      targetValueCashRaw: NonNegativeIntegerAmountSchema,
    }),
  ),
});

export const ExecutionOndoDirectRebalancePlanPreviewSchema = z.object({
  flow: z.literal("ondo_direct_rebalance_plan"),
  provider: z.literal("ondo_direct"),
  portfolioId: z.string(),
  chainId: z.number().int().positive(),
  cashAssetId: z.string(),
  cashTokenAddress: EvmAddressSchema,
  vaultAddress: EvmAddressSchema,
  plan: OndoDirectRebalancePlanSchema,
});

const BebopRfqQuoteTokenSchema = z
  .object({
    amount: z.string().min(1),
    amountBeforeFee: z.string().min(1).nullable().optional(),
    decimals: z.number().int().nonnegative(),
    deltaFromExpected: z.number().nullable().optional(),
    minimumAmount: z.string().min(1).optional(),
    price: z.number().nullable().optional(),
    priceBeforeFee: z.number().nullable().optional(),
    priceUsd: z.number().nullable().optional(),
    symbol: z.string().min(1),
  })
  .passthrough();

const BebopRfqQuoteTokenMapSchema = z.record(
  z.string(),
  BebopRfqQuoteTokenSchema,
);

const ExecutionMarketOrderQuotePreviewBaseSchema = z.object({
  flow: z.literal("market_order"),
  executable: z.boolean(),
  executionUnavailableReason: z.string().min(1).nullable(),
  executionUnavailableDetail: z.string().min(1).nullable(),
  portfolioId: z.string().min(1),
  chainId: z.number().int().positive(),
  network: z.string().min(1),
  sellAssetId: z.string().min(1),
  buyAssetId: z.string().min(1),
  sellAmount: z.string().regex(/^(?:[1-9]\d*)$/u),
  vaultAddress: EvmAddressSchema,
  taker: EvmAddressSchema,
  receiver: EvmAddressSchema,
  recipient: EvmAddressSchema,
  recipientPortfolioId: z.string().min(1).nullable(),
  venue: ExpectedMarketOrderVenueSchema.optional(),
  cached: z.boolean().optional(),
  stale: z.boolean().optional(),
  quoteAgeMs: z.number().int().nonnegative().optional(),
  refreshFailedReason: z.string().min(1).nullable().optional(),
});

const ExecutionBebopMarketOrderQuotePreviewSchema =
  ExecutionMarketOrderQuotePreviewBaseSchema.extend({
    provider: z.literal("bebop_rfq"),
    executionUnavailableReason: z
      .enum([
        "invalid_sell_amount",
        "invalid_sell_token",
        "required_signatures",
        "unsupported_approval_type",
      ])
      .nullable(),
    quote: z.object({
      quoteId: z.string().min(1),
      requestId: z.string().min(1),
      expiry: z.number().int().positive(),
      expiryType: z.enum(["standard", "short"]),
      slippage: z.number(),
      priceImpact: z.number().nullable().optional(),
      gasFee: z.object({
        native: z.string().min(1),
        usd: z.number().nullable().optional(),
      }),
      warnings: z.array(
        z.object({
          code: z.number(),
          message: z.string(),
        }),
      ),
      sellTokens: BebopRfqQuoteTokenMapSchema,
      buyTokens: BebopRfqQuoteTokenMapSchema,
      approvalType: z.enum(["Standard", "Permit", "Permit2"]),
      approvalTarget: EvmAddressSchema,
      settlementAddress: EvmAddressSchema,
    }),
  });

const ExecutionBebopJamMarketOrderQuotePreviewSchema =
  ExecutionMarketOrderQuotePreviewBaseSchema.extend({
    provider: z.literal("bebop_jam"),
    executionUnavailableReason: z
      .enum([
        "invalid_sell_amount",
        "invalid_sell_token",
        "required_signatures",
        "unsupported_approval_type",
      ])
      .nullable(),
    quote: z.object({
      quoteId: z.string().min(1),
      requestId: z.string().min(1).nullable(),
      expiry: z.number().int().positive(),
      slippage: z.number(),
      priceImpact: z.number().nullable().optional(),
      gasFee: z.object({
        native: z.string().min(1),
        usd: z.number().nullable().optional(),
      }),
      warnings: z.array(
        z.object({
          code: z.number(),
          message: z.string(),
        }),
      ),
      sellTokens: BebopRfqQuoteTokenMapSchema,
      buyTokens: BebopRfqQuoteTokenMapSchema,
      approvalType: z.enum(["Standard", "Permit", "Permit2"]),
      approvalTarget: EvmAddressSchema,
      settlementAddress: EvmAddressSchema,
      solver: z.string().min(1).nullable().optional(),
    }),
  });

const ExecutionOndoDirectMarketOrderQuotePreviewSchema =
  ExecutionMarketOrderQuotePreviewBaseSchema.extend({
    provider: z.literal("ondo_direct"),
    executionUnavailableReason: z.null(),
    executionUnavailableDetail: z.null(),
    estimatedReceiveAmount: z.string().min(1),
    estimatedReceiveAssetId: z.string().min(1),
    estimatedReceiveDecimals: z.number().int().nonnegative(),
    estimatedReceiveSymbol: z.string().min(1),
    quote: z
      .object({
        chainId: z.string().min(1),
        symbol: z.string().min(1),
        ticker: z.string().min(1),
        assetAddress: z.string().min(1),
        side: z.enum(["buy", "sell"]),
        tokenAmount: z.string().min(1),
        price: z.string().min(1),
        notionalValue: z.string().min(1),
        cashAddress: EvmAddressSchema,
        cashAssetId: z.string().min(1),
        cashSymbol: z.string().min(1),
        managerAddress: EvmAddressSchema,
      })
      .passthrough(),
  });

const ExecutionLifiMarketOrderQuotePreviewSchema =
  ExecutionMarketOrderQuotePreviewBaseSchema.extend({
    provider: z.literal("lifi"),
    venueKey: LifiMarketOrderVenueKeySchema,
    executionUnavailableReason: z.null(),
    executionUnavailableDetail: z.null(),
    estimatedReceiveAmount: z.string().min(1),
    estimatedReceiveAssetId: z.string().min(1),
    estimatedReceiveDecimals: z.number().int().nonnegative().nullable(),
    estimatedReceiveSymbol: z.string().min(1).nullable(),
    quote: z
      .object({
        routeId: z.string().min(1),
        fromChainId: z.number().int().positive(),
        toChainId: z.number().int().positive(),
        fromTokenAddress: EvmAddressSchema,
        toTokenAddress: EvmAddressSchema,
        fromAmount: z.string().min(1),
        toAmount: z.string().min(1),
        stepCount: z.number().int().positive(),
      })
      .passthrough(),
  });

export const ExecutionMarketOrderQuotePreviewSchema = z
  .discriminatedUnion("provider", [
    ExecutionBebopMarketOrderQuotePreviewSchema,
    ExecutionBebopJamMarketOrderQuotePreviewSchema,
    ExecutionOndoDirectMarketOrderQuotePreviewSchema,
    ExecutionLifiMarketOrderQuotePreviewSchema,
  ])
  .superRefine((preview, context) => {
    if (preview.venue && preview.venue.provider !== preview.provider) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "venue provider must match quote provider",
        path: ["venue", "provider"],
      });
    }
  });

const SpecDrivenBuyFundPlanSchema = z
  .object({
    kind: z.literal("fund"),
    requiredAssetId: z.string().min(1),
    requiredAmountRaw: PositiveIntegerAmountSchema,
    legs: z.array(SpecDrivenBuyFundLegWireSchema).min(1).max(8),
    constraints: z
      .object({
        maxFundingCostBps: z.number().int().min(0).max(1_000),
        fundingTimeoutAt: z.string().datetime({ offset: true }),
        topUp: z
          .object({ maxAttempts: z.number().int().min(0).max(3) })
          .strict(),
      })
      .strict(),
  })
  .strict();

const SpecDrivenBuyFundingExplanationSchema = z
  .object({
    requiredAssetId: z.string().min(1),
    requiredAssetDecimals: z.number().int().nonnegative(),
    destinationSpendableRaw: NonNegativeIntegerAmountSchema,
    nettedRaw: NonNegativeIntegerAmountSchema,
    shortfallRaw: NonNegativeIntegerAmountSchema,
    terminalDeliverableRaw: NonNegativeIntegerAmountSchema,
    legs: z.array(
      z
        .object({
          source: z
            .object({
              chainRef: z.string().min(1),
              accountRef: z.string().min(1),
              assetId: z.string().min(1),
              symbol: z.string().min(1),
              decimals: z.number().int().nonnegative(),
              amountRaw: PositiveIntegerAmountSchema,
            })
            .strict(),
          terminal: z
            .object({
              assetId: z.string().min(1),
              decimals: z.number().int().nonnegative(),
              minAmountRaw: PositiveIntegerAmountSchema,
            })
            .strict(),
          rail: z.enum([
            "zerodev_routing_address",
            "lifi_bridge",
            "same_chain_swap",
            "float_transfer",
          ]),
          slippageBps: z.number().int().min(0).max(10_000).optional(),
          railQuoteRef: z.string().min(1).optional(),
          expectedCostBps: z.number().int().nonnegative(),
          etaSeconds: z.number().int().nonnegative().optional(),
          paddingBps: z.number().int().nonnegative(),
          expectedCostScore: NonNegativeIntegerAmountSchema,
        })
        .strict(),
    ),
    candidatePlanCount: z.number().int().nonnegative(),
    searchTruncation: z
      .object({
        maxSourcesPerAssetChain: z.number().int().positive(),
        omittedSourceCount: z.number().int().nonnegative(),
        groups: z.array(
          z
            .object({
              chainRef: z.string().min(1),
              assetId: z.string().min(1),
              sourceCount: z.number().int().nonnegative(),
              consideredSourceCount: z.number().int().nonnegative(),
            })
            .strict(),
        ),
        quoteSearch: z
          .object({
            reason: z.literal("quote_replan_budget_exhausted"),
            maxReplanIterations: z.number().int().positive(),
            untriedCandidateCount: z.number().int().positive(),
          })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
    decidedBy: z.discriminatedUnion("criterion", [
      z.object({ criterion: z.literal("only_viable_plan") }).strict(),
      z.object({ criterion: z.literal("no_viable_plan") }).strict(),
      z
        .object({
          criterion: z.literal("max_terminal_deliverable"),
          winnerTerminalRaw: NonNegativeIntegerAmountSchema,
          runnerUpTerminalRaw: NonNegativeIntegerAmountSchema,
        })
        .strict(),
      z
        .object({
          criterion: z.literal("fewest_legs"),
          winnerLegCount: z.number().int().nonnegative(),
          runnerUpLegCount: z.number().int().nonnegative(),
        })
        .strict(),
      z
        .object({
          criterion: z.literal("lowest_expected_cost"),
          commonDecimals: z.number().int().nonnegative(),
          winnerExpectedCostScore: NonNegativeIntegerAmountSchema,
          runnerUpExpectedCostScore: NonNegativeIntegerAmountSchema,
        })
        .strict(),
      z
        .object({
          criterion: z.literal("priority_order"),
          winnerPriorityOrder: z.array(z.number().int().nonnegative()),
          runnerUpPriorityOrder: z.array(z.number().int().nonnegative()),
        })
        .strict(),
      z
        .object({
          criterion: z.literal("deterministic_tie_break"),
          winnerKey: z.string(),
          runnerUpKey: z.string(),
        })
        .strict(),
    ]),
    humanExplanation: z.string().min(1),
  })
  .strict();

/**
 * Immutable preview material handed back to create. A fresh basis lets the
 * engine reuse the exact quoted funding plan; an expired basis is only a
 * replan hint and never bypasses create-time admission validation.
 */
const ExecutionSpecDrivenBuyPlanBasisIntegritySchema = z
  .string()
  .regex(/^sbb1\.[a-f0-9]{64}$/u);

export const ExecutionSpecDrivenBuyPlanBasisSchema =
  SpecDrivenBuyPlanBasisSchema;

const SpecDrivenBuyPreviewResponseBaseSchema = z.object({
  buyingPowerRaw: NonNegativeIntegerAmountSchema,
  explanation: SpecDrivenBuyFundingExplanationSchema,
  waveCount: z.number().int().nonnegative(),
  planBasis: ExecutionSpecDrivenBuyPlanBasisSchema.nullable(),
});

export const ExecutionSpecDrivenBuyPreviewResponseSchema = z.union([
  SpecDrivenBuyPreviewResponseBaseSchema.extend({
    plan: SpecDrivenBuyFundPlanSchema.nullable(),
  }).strict(),
  SpecDrivenBuyPreviewResponseBaseSchema.extend({
    bestPartial: SpecDrivenBuyFundPlanSchema.nullable(),
  }).strict(),
]);

export const ExecutionSpecDrivenBuyCreatedFundingSchema =
  ExecutionSpecDrivenBuyPlanBasisSchema.extend({
    integrity: ExecutionSpecDrivenBuyPlanBasisIntegritySchema.optional(),
    basisReused: z.boolean(),
    spendRaw: PositiveIntegerAmountSchema,
  }).strict();

export const ExecutionSpecDrivenBuyOperationResponseSchema =
  ExecutionOperationResponseSchema.extend({
    flow: z.literal("spec_driven_buy"),
    funding: ExecutionSpecDrivenBuyCreatedFundingSchema.nullable(),
  });

export const ExecutionSpecDrivenBuyFundingRecoveryResponseSchema = z
  .object({
    funding: ExecutionSpecDrivenBuyCreatedFundingSchema.nullable(),
  })
  .strict();

export const ExecutionOperationEventAckSchema = z.object({
  operationId: z.string().min(1),
  workflowId: z.string().min(1),
  accepted: z.boolean(),
  eventType: z.string().min(1),
  state: ExecutionStateSchema,
});

const ExecutionGraphNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  kind: z.string(),
  status: z.enum(["pending", "active", "completed", "failed"]).optional(),
  chainId: z.string().nullable().optional(),
  assetId: z.string().nullable().optional(),
  metadata: ExecutionLiveJsonObjectSchema.nullable().optional(),
});

const ExecutionGraphSchema: z.ZodType<OperationExecutionGraph> = z.object({
  nodes: z.array(ExecutionGraphNodeSchema),
  edges: z.array(
    z.object({
      from: z.string(),
      to: z.string(),
    }),
  ),
});

const ExecutionPortfolioScopeSchema = z.object({
  portfolioId: z.string().min(1),
  role: z.enum(["primary", "source", "destination"]),
});

const ExecutionResourceScopeSchema = z.object({
  resourceKey: z.string().min(1),
  kind: z.enum(["portfolio", "asset"]),
  portfolioId: z.string().min(1),
  role: z.enum(["debit", "credit", "exclusive"]),
  chainId: z.number().int().positive().nullable().optional(),
  assetId: z.string().nullable().optional(),
});

const ExecutionTransferRefSchema = z.object({
  sourcePortfolioId: z.string().min(1),
  destinationPortfolioId: z.string().min(1),
  assetId: z.string().min(1),
  amount: z.string().min(1),
  assets: z
    .array(
      z.object({
        assetId: z.string().min(1),
        amount: z.string().min(1),
      }),
    )
    .nullable()
    .optional(),
  displayAmount: z.string().nullable().optional(),
  assetSymbol: z.string().nullable().optional(),
  sourcePortfolioName: z.string().nullable().optional(),
  destinationPortfolioName: z.string().nullable().optional(),
});

const ExecutionWithdrawRefSchema = z.object({
  sourcePortfolioId: z.string().min(1),
  chainId: z.number().int().positive(),
  assetId: z.string().min(1),
  amount: z.string().min(1),
  // The projection strips the raw recipient for browser-bound frames. Keep
  // this optional so snapshots written before that redaction continue to
  // parse while they drain from Durable Object storage.
  recipientAddress: z.string().min(1).optional(),
  displayAmount: z.string().nullable().optional(),
  assetSymbol: z.string().nullable().optional(),
  sourcePortfolioName: z.string().nullable().optional(),
  recipientLabel: z.string().nullable().optional(),
});

const ExecutionMarketOrderRefSchema = z.object({
  flow: z.literal("market_order"),
  provider: z.enum([
    "bebop_rfq",
    "bebop_jam",
    "ondo_direct",
    "reality_via_bitget",
    "lifi",
  ]),
  intentSide: z.enum(["buy", "sell", "swap"]),
  chainId: z.number().int().positive(),
  sellAssetId: z.string().min(1),
  buyAssetId: z.string().min(1),
  sellAmount: z.string().min(1),
  cashAssetId: z.string().nullable(),
  quoteAssetId: z.string().nullable(),
});

const ExecutionBridgeRefSchema = z.object({
  portfolioId: z.string().min(1),
  sourceChainId: z.number().int().positive(),
  destinationChainId: z.number().int().positive(),
  assetId: z.string().min(1),
  destinationAssetId: z.string().min(1),
  amount: z.string().min(1),
  routeProvider: z.literal("lifi"),
  routeId: z.string().nullable().optional(),
  bridgeTool: z.string().nullable().optional(),
  sourceVaultAddress: z.string().nullable().optional(),
  sourceTxHash: z.string().nullable().optional(),
  destinationTxHash: z.string().nullable().optional(),
  displayAmount: z.string().nullable().optional(),
  assetSymbol: z.string().nullable().optional(),
});

const ExecutionFloatRecoverySettlementRefSchema = z.object({
  advanceId: z.string().min(1),
  chainRefs: z.array(z.string().min(1)).min(1),
});

/**
 * Public refs shared by the Worker and browser client. Unknown JSON-valued
 * refs survive forward-compatible readers. Raw executionIntent is stripped
 * here as defense in depth in addition to the server projection.
 */
const ExecutionLiveRefsWireShape = {
  workflowId: z.string().nullable().optional(),
  runId: z.string().nullable().optional(),
  eventId: z.string().nullable().optional(),
  initiatorUserId: z.string().nullable().optional(),
  capabilityRef: z
    .object({
      jti: z.string(),
      sessionRef: z.string().optional(),
    })
    .nullable()
    .optional(),
  legacyOperationId: z.string().nullable().optional(),
  requestKey: z.string().nullable().optional(),
  activityFailureCode: z
    .string()
    .regex(EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN)
    .nullable()
    .optional(),
  correlationId: z.string().nullable().optional(),
  scheduleId: z.string().nullable().optional(),
  scheduledFunctionId: z.string().nullable().optional(),
  scheduledRunAt: z.string().nullable().optional(),
  requestId: z.string().nullable().optional(),
  executionSource: z
    .enum(["manual", "scheduled", "event", "scheduled_function"])
    .nullable()
    .optional(),
  portfolioScopes: z.array(ExecutionPortfolioScopeSchema).nullable().optional(),
  resourceScopes: z.array(ExecutionResourceScopeSchema).nullable().optional(),
  transfer: ExecutionTransferRefSchema.nullable().optional(),
  withdraw: ExecutionWithdrawRefSchema.nullable().optional(),
  marketOrder: ExecutionMarketOrderRefSchema.nullable().optional(),
  bridge: ExecutionBridgeRefSchema.nullable().optional(),
  floatRecoverySettlement:
    ExecutionFloatRecoverySettlementRefSchema.nullable().optional(),
  // Explicitly admit then remove these internal fields before the catchall so
  // forward-compatible refs cannot bypass the browser redaction boundary.
  executionIntent: z.unknown().optional(),
  workflowParams: z.unknown().optional(),
  workflowVersion: z.unknown().optional(),
} satisfies z.ZodRawShape;

const ExecutionLiveRefsWireSchema = z
  .object(ExecutionLiveRefsWireShape)
  .catchall(ExecutionLiveJsonValueSchema);

export const ExecutionLiveRefsSchema = ExecutionLiveRefsWireSchema.transform(
  (refs): ExecutionLiveRefs => {
    const {
      executionIntent: _executionIntent,
      workflowParams: _workflowParams,
      workflowVersion: _workflowVersion,
      ...publicRefs
    } = refs;
    return publicRefs;
  },
);

export const ExecutionLivePublishRefsSchema = z
  .object(ExecutionLiveRefsWireShape)
  .transform((refs): ExecutionLiveRefs => {
    const {
      executionIntent: _executionIntent,
      workflowParams: _workflowParams,
      workflowVersion: _workflowVersion,
      ...publicRefs
    } = refs;
    return publicRefs;
  });

export const ExecutionLiveOperationSchema = z.object({
  operationId: z.string(),
  runId: z.string(),
  portfolioId: z.string(),
  kind: z.enum([
    "rebalance",
    "deposit",
    "withdraw",
    "transfer",
    "bridge",
    "swap",
    "execution",
  ]),
  state: ExecutionStateSchema,
  title: z.string(),
  currentHeadline: z.string().nullable(),
  currentDetail: z.string().nullable(),
  currentPhase: z.string().nullable(),
  // Optional while old Durable Object snapshots drain. New projections always
  // emit the stable semantic phase; currentPhase remains for old clients.
  activityPhase: z.enum(EXECUTION_ACTIVITY_PHASES).optional(),
  failureCode: z
    .string()
    .regex(EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN)
    .nullable()
    .optional(),
  currentStep: z.number().int().nullable(),
  totalSteps: z.number().int().nullable(),
  refs: ExecutionLiveRefsSchema,
  viewKind: z.enum(["timeline", "observer_graph"]),
  graph: ExecutionGraphSchema.nullable(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ExecutionLiveTimelineEventSchema = z.object({
  id: z.string(),
  operationId: z.string(),
  runId: z.string(),
  sequence: z.number().int().nonnegative(),
  dedupeKey: z.string(),
  kind: z.string(),
  eventKind: z.enum(EXECUTION_ACTIVITY_EVENT_KINDS).optional(),
  activityPhase: z.enum(EXECUTION_ACTIVITY_PHASES).optional(),
  failureCode: z
    .string()
    .regex(EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN)
    .nullable()
    .optional(),
  title: z.string(),
  detail: z.string().nullable(),
  phase: z.string().nullable(),
  status: z
    .enum([
      "pending",
      "active",
      "waiting",
      "blocked",
      "completed",
      "failed",
      "info",
    ])
    .nullable(),
  groupKey: z.string().nullable(),
  parentGroupKey: z.string().nullable(),
  laneKey: z.string().nullable(),
  refs: ExecutionLiveJsonObjectSchema.nullable(),
  data: ExecutionLiveJsonObjectSchema.nullable(),
  createdAt: z.string(),
});

export const ExecutionLiveSnapshotSchema = z.object({
  kind: z.literal("execution.snapshot"),
  operation: ExecutionLiveOperationSchema,
  events: z.array(ExecutionLiveTimelineEventSchema),
  sequence: z.number().int().nonnegative(),
  ts: z.string(),
});

export const ExecutionLiveEventSchema = z.object({
  kind: z.literal("execution.event"),
  event: ExecutionLiveTimelineEventSchema,
  operation: ExecutionLiveOperationSchema,
  sequence: z.number().int().nonnegative(),
  ts: z.string(),
});

export const ExecutionLiveProjectionSchema = z.object({
  snapshot: ExecutionLiveSnapshotSchema,
  event: ExecutionLiveEventSchema.optional(),
});

const LiveClientControlSchemas = [
  z.object({
    kind: z.literal("hello"),
    lastSequence: z.number().int().nonnegative().optional(),
  }),
  // Reserved for future backpressure/retention tuning. Durable Objects accept
  // and ignore it today so newer clients can safely send optimistic acks.
  z.object({
    kind: z.literal("ack"),
    sequence: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal("ping"),
    clientTs: z.number().finite(),
  }),
] as const;

export const ExecutionLiveClientMessageSchema = z.discriminatedUnion(
  "kind",
  LiveClientControlSchemas,
);

const LiveServerControlSchemas = [
  z.object({
    kind: z.literal("pong"),
    clientTs: z.number().finite(),
    serverTs: z.number().finite(),
  }),
  z.object({
    kind: z.literal("error"),
    code: z.string(),
    message: z.string(),
  }),
] as const;

export const ExecutionLiveServerMessageSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("snapshot"),
    sequence: z.number().int().nonnegative(),
    payload: ExecutionLiveSnapshotSchema,
  }),
  z.object({
    kind: z.literal("event"),
    sequence: z.number().int().nonnegative(),
    payload: ExecutionLiveEventSchema,
  }),
  ...LiveServerControlSchemas,
]);

export const PortfolioExecutionFeedSnapshotSchema = z.object({
  kind: z.literal("portfolio.execution_feed.snapshot"),
  portfolioId: z.string().min(1),
  operations: z.array(ExecutionLiveOperationSchema),
  sequence: z.number().int().nonnegative(),
  ts: z.string(),
});

export const PortfolioExecutionFeedOperationSchema = z.object({
  kind: z.literal("portfolio.execution_feed.operation"),
  portfolioId: z.string().min(1),
  operation: ExecutionLiveOperationSchema,
  sequence: z.number().int().nonnegative(),
  ts: z.string(),
});

export const PortfolioExecutionFeedProjectionSchema = z.object({
  snapshot: PortfolioExecutionFeedSnapshotSchema,
  operation: PortfolioExecutionFeedOperationSchema.optional(),
});

export const PortfolioExecutionFeedHydrateInputSchema = z.object({
  portfolioId: z.string().min(1),
  operations: z.array(ExecutionLiveOperationSchema),
});

export const PortfolioExecutionFeedClientMessageSchema = z.discriminatedUnion(
  "kind",
  LiveClientControlSchemas,
);

export const PortfolioExecutionFeedServerMessageSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: PortfolioExecutionFeedSnapshotSchema,
    }),
    z.object({
      kind: z.literal("operation"),
      sequence: z.number().int().nonnegative(),
      payload: PortfolioExecutionFeedOperationSchema,
    }),
    ...LiveServerControlSchemas,
  ],
);

export const UserExecutionFeedSnapshotSchema = z.object({
  kind: z.literal("user.execution_feed.snapshot"),
  userId: z.string().min(1),
  operations: z.array(ExecutionLiveOperationSchema),
  sequence: z.number().int().nonnegative(),
  ts: z.string(),
});

export const UserExecutionFeedOperationSchema = z.object({
  kind: z.literal("user.execution_feed.operation"),
  userId: z.string().min(1),
  operation: ExecutionLiveOperationSchema,
  sequence: z.number().int().nonnegative(),
  ts: z.string(),
});

export const UserExecutionFeedProjectionSchema = z.object({
  snapshot: UserExecutionFeedSnapshotSchema,
  operation: UserExecutionFeedOperationSchema.optional(),
});

export const UserExecutionFeedHydrateInputSchema = z.object({
  userId: z.string().min(1),
  operations: z.array(ExecutionLiveOperationSchema),
});

export const UserExecutionFeedClientMessageSchema = z.discriminatedUnion(
  "kind",
  LiveClientControlSchemas,
);

export const UserExecutionFeedServerMessageSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: UserExecutionFeedSnapshotSchema,
    }),
    z.object({
      kind: z.literal("operation"),
      sequence: z.number().int().nonnegative(),
      payload: UserExecutionFeedOperationSchema,
    }),
    ...LiveServerControlSchemas,
  ],
);

export const ExecutionLivePublishOperationSchema =
  ExecutionLiveOperationSchema.extend({ refs: ExecutionLivePublishRefsSchema });

export const ExecutionLivePublishSnapshotSchema =
  ExecutionLiveSnapshotSchema.extend({
    operation: ExecutionLivePublishOperationSchema,
  });

export const ExecutionLivePublishEventSchema = ExecutionLiveEventSchema.extend({
  operation: ExecutionLivePublishOperationSchema,
});

export const ExecutionLivePublishProjectionSchema = z.object({
  snapshot: ExecutionLivePublishSnapshotSchema,
  event: ExecutionLivePublishEventSchema.optional(),
});

export const ExecutionLivePublishServerMessageSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: ExecutionLivePublishSnapshotSchema,
    }),
    z.object({
      kind: z.literal("event"),
      sequence: z.number().int().nonnegative(),
      payload: ExecutionLivePublishEventSchema,
    }),
    ...LiveServerControlSchemas,
  ],
);

export const PortfolioExecutionFeedPublishSnapshotSchema =
  PortfolioExecutionFeedSnapshotSchema.extend({
    operations: z.array(ExecutionLivePublishOperationSchema),
  });

export const PortfolioExecutionFeedPublishOperationSchema =
  PortfolioExecutionFeedOperationSchema.extend({
    operation: ExecutionLivePublishOperationSchema,
  });

export const PortfolioExecutionFeedPublishProjectionSchema = z.object({
  snapshot: PortfolioExecutionFeedPublishSnapshotSchema,
  operation: PortfolioExecutionFeedPublishOperationSchema.optional(),
});

export const PortfolioExecutionFeedPublishHydrateInputSchema =
  PortfolioExecutionFeedHydrateInputSchema.extend({
    operations: z.array(ExecutionLivePublishOperationSchema),
  });

export const PortfolioExecutionFeedPublishServerMessageSchema =
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: PortfolioExecutionFeedPublishSnapshotSchema,
    }),
    z.object({
      kind: z.literal("operation"),
      sequence: z.number().int().nonnegative(),
      payload: PortfolioExecutionFeedPublishOperationSchema,
    }),
    ...LiveServerControlSchemas,
  ]);

export const UserExecutionFeedPublishSnapshotSchema =
  UserExecutionFeedSnapshotSchema.extend({
    operations: z.array(ExecutionLivePublishOperationSchema),
  });

export const UserExecutionFeedPublishOperationSchema =
  UserExecutionFeedOperationSchema.extend({
    operation: ExecutionLivePublishOperationSchema,
  });

export const UserExecutionFeedPublishProjectionSchema = z.object({
  snapshot: UserExecutionFeedPublishSnapshotSchema,
  operation: UserExecutionFeedPublishOperationSchema.optional(),
});

export const UserExecutionFeedPublishHydrateInputSchema =
  UserExecutionFeedHydrateInputSchema.extend({
    operations: z.array(ExecutionLivePublishOperationSchema),
  });

export const UserExecutionFeedPublishServerMessageSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: UserExecutionFeedPublishSnapshotSchema,
    }),
    z.object({
      kind: z.literal("operation"),
      sequence: z.number().int().nonnegative(),
      payload: UserExecutionFeedPublishOperationSchema,
    }),
    ...LiveServerControlSchemas,
  ],
);

export type ExecutionOperationResponse = z.infer<
  typeof ExecutionOperationResponseSchema
>;
export type ExecutionRebalanceCreateResponse = z.infer<
  typeof ExecutionRebalanceCreateResponseSchema
>;
export type ExecutionLiquidationCreateResponse = z.infer<
  typeof ExecutionLiquidationCreateResponseSchema
>;
export type ExecutionLiquidationOperationAck = z.infer<
  typeof ExecutionLiquidationOperationAckSchema
>;
export type LiveDescriptor = z.infer<typeof LiveDescriptorSchema>;
export type UserFeedLiveDescriptor = z.infer<
  typeof UserFeedLiveDescriptorSchema
>;
export type PortfolioFeedLiveDescriptor = z.infer<
  typeof PortfolioFeedLiveDescriptorSchema
>;
export type ExecutionOperationEventAck = z.infer<
  typeof ExecutionOperationEventAckSchema
>;
export type ExecutionLiveOperation = z.infer<
  typeof ExecutionLiveOperationSchema
>;
export type ExecutionLiveTimelineEvent = z.infer<
  typeof ExecutionLiveTimelineEventSchema
>;
export type ExecutionLiveSnapshot = z.infer<typeof ExecutionLiveSnapshotSchema>;
export type ExecutionLiveEvent = z.infer<typeof ExecutionLiveEventSchema>;
export type ExecutionLiveProjection = z.infer<
  typeof ExecutionLiveProjectionSchema
>;
export type ExecutionLiveClientMessage = z.infer<
  typeof ExecutionLiveClientMessageSchema
>;
export type ExecutionLiveServerMessage = z.infer<
  typeof ExecutionLiveServerMessageSchema
>;
export type PortfolioExecutionFeedSnapshot = z.infer<
  typeof PortfolioExecutionFeedSnapshotSchema
>;
export type PortfolioExecutionFeedOperation = z.infer<
  typeof PortfolioExecutionFeedOperationSchema
>;
export type PortfolioExecutionFeedProjection = z.infer<
  typeof PortfolioExecutionFeedProjectionSchema
>;
export type PortfolioExecutionFeedHydrateInput = z.infer<
  typeof PortfolioExecutionFeedHydrateInputSchema
>;
export type PortfolioExecutionFeedClientMessage = z.infer<
  typeof PortfolioExecutionFeedClientMessageSchema
>;
export type PortfolioExecutionFeedServerMessage = z.infer<
  typeof PortfolioExecutionFeedServerMessageSchema
>;
export type UserExecutionFeedSnapshot = z.infer<
  typeof UserExecutionFeedSnapshotSchema
>;
export type UserExecutionFeedOperation = z.infer<
  typeof UserExecutionFeedOperationSchema
>;
export type UserExecutionFeedProjection = z.infer<
  typeof UserExecutionFeedProjectionSchema
>;
export type UserExecutionFeedHydrateInput = z.infer<
  typeof UserExecutionFeedHydrateInputSchema
>;
export type UserExecutionFeedClientMessage = z.infer<
  typeof UserExecutionFeedClientMessageSchema
>;
export type UserExecutionFeedServerMessage = z.infer<
  typeof UserExecutionFeedServerMessageSchema
>;

export type ExecutionLiveOperationRuntimeTypes = {
  readonly kind: OperationExecutionKind;
  readonly state: OperationExecutionState;
  readonly refs: ExecutionLiveRefs;
  readonly viewKind: OperationExecutionViewKind;
  readonly graph: OperationExecutionGraph | null;
  readonly eventStatus: OperationExecutionEventStatus | null;
};
